import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  Link as MuiLink,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import BuildIcon from "@mui/icons-material/Build";
import BrushIcon from "@mui/icons-material/Brush";
import NatureIcon from "@mui/icons-material/Nature";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WarningIcon from "@mui/icons-material/Warning";
import TimelineIcon from "@mui/icons-material/Timeline";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForward";
import LaunchIcon from "@mui/icons-material/Launch";
import InfoBox from "../utils/InfoBox";
import { getImageUrl } from "../../../utils/helpers";

import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

const KthPatentProsecution = ({ baseConfig, sectionStyles }) => {
  const {
    priColor,
    priHoverColor,
    tertColor,
    tertHoverColor,
    secColor,
    quadColor,
    quadHoverColor,
    linkColor,
  } = baseConfig.siteBranding;

  return (
    <Card sx={{ ...sectionStyles.card, mb: 2 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: `${tertColor}22`,
        }}
      >
        <DescriptionIcon sx={{ mr: 2, color: tertColor }} />
        <Typography variant="h6" fontWeight="bold">
          Patent Drafting & Prosecution
        </Typography>
      </Box>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body1" paragraph>
              Our patent drafting and prosecution services are tailored to
              engineering and technology companies seeking comprehensive
              protection for their innovations. We understand the technical
              nuances of your inventions and translate them into robust patent
              claims that withstand scrutiny and maximize value.
            </Typography>

            <Typography variant="body1" paragraph>
              Using our deep engineering background, we draft patents that
              anticipate future technological developments and protect your
              competitive edge. Our strategic prosecution approach achieves
              broad claim scope while minimizing prosecution history estoppel
              risks.
            </Typography>

            <Button
              startIcon={<PhoneInTalkIcon />}
              variant="contained"
              onClick={() => scrollToSection("contact-section")}
              sx={{
                mt: 4,
                backgroundColor: tertColor,
                color: "white",
                "&:hover": {
                  color: "white",
                  backgroundColor: tertHoverColor,
                },
              }}
            >
              Schedule a Consultation
            </Button>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <img
                src={getImageUrl("tech-patent-drafting.png")}
                alt="Patent Drafting & Prosecution"
                style={{
                  width: "100%",
                  maxWidth: "450px",
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                color={tertColor}
              >
                Our Patent Drafting & Prosecution Services:
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Strategic patent filing strategies tailored to your business goals" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Comprehensive prior art searches and analysis" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Provisional and non-provisional application preparation" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Office action responses and examiner interviews" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Continuation practice and portfolio development" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="International patent protection strategies" />
                </ListItem>
              </List>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${tertColor}33`,
              }}
            >
              <Typography variant="h6" gutterBottom color={tertColor}>
                Why Our Patent Drafting Approach Is Different
              </Typography>
              <Typography variant="body2" paragraph>
                As engineers and technology experts ourselves, we understand the
                technical aspects of your innovations at a deeper level than
                general practice firms. This allows us to:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Craft claims that accurately capture the inventive concept while maximizing scope" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Anticipate technological evolutions and include forward-looking claim language" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Create detailed specifications that enable robust fallback positions during prosecution" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ChevronRightIcon sx={{ color: tertColor }} />
                  </ListItemIcon>
                  <ListItemText primary="Develop enforcement-ready patents that withstand litigation challenges" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        {/* Types of Patents Section */}
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: priColor,
              borderBottom: `1px solid ${tertColor}33`,
              pb: 1,
            }}
          >
            Types of Patents
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  mt: 2,
                  height: "100%",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                  border: `1px solid ${tertColor}33`,
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <BuildIcon
                      sx={{ color: tertColor, mr: 1.5, fontSize: "2rem" }}
                    />
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color={tertColor}
                    >
                      Utility Patents
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" paragraph>
                    Protect the functional aspects of an invention, including
                    processes, machines, manufactures, compositions of matter,
                    and improvements thereof. Utility patents are the most
                    common type of patent.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Duration:</strong> 20 years from filing date
                  </Typography>
                  <Typography variant="body2">
                    <strong>Best for:</strong> Technical innovations, methods,
                    systems, devices, software-implemented inventions, and
                    technological improvements.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  mt: 2,
                  height: "100%",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                  border: `1px solid ${tertColor}33`,
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <BrushIcon
                      sx={{ color: tertColor, mr: 1.5, fontSize: "2rem" }}
                    />
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color={tertColor}
                    >
                      Design Patents
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" paragraph>
                    Protect the ornamental design or appearance of an article of
                    manufacture, rather than its functional aspects. Design
                    patents can complement utility patents and{" "}
                    <RouterLink to="/trademarks" style={{ color: linkColor }}>
                      trademarks
                    </RouterLink>{" "}
                    in a comprehensive IP strategy.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Duration:</strong> 15 years from issue date
                  </Typography>
                  <Typography variant="body2">
                    <strong>Best for:</strong> Product appearances, user
                    interfaces, icons, graphical elements, and distinctive
                    visual features.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  mt: 2,
                  height: "100%",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                  border: `1px solid ${tertColor}33`,
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <NatureIcon
                      sx={{ color: tertColor, mr: 1.5, fontSize: "2rem" }}
                    />
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color={tertColor}
                    >
                      Plant Patents
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" paragraph>
                    Protect new varieties of plants that have been asexually
                    reproduced. While less common in technology contexts, plant
                    patents may be relevant for agricultural technology
                    companies.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Duration:</strong> 20 years from filing date
                  </Typography>
                  <Typography variant="body2">
                    <strong>Best for:</strong> New plant varieties, cultivars,
                    and agricultural innovations involving novel plant types.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Types of Utility Patents */}
        <Box sx={{ mt: 6 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: priColor,
              borderBottom: `1px solid ${tertColor}33`,
              pb: 1,
            }}
          >
            Types of Utility Patent Applications
          </Typography>

          <Grid container spacing={3} sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  mt: 2,
                  height: "100%",
                  border: `1px solid ${tertColor}33`,
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <BusinessCenterIcon sx={{ color: tertColor, mr: 1.5 }} />
                    <Typography
                      variant="h6"
                      fontWeight="medium"
                      color={tertColor}
                    >
                      Provisional Applications
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: tertColor }} />
                      </ListItemIcon>
                      <ListItemText primary="Serves as a placeholder that establishes a priority date" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: tertColor }} />
                      </ListItemIcon>
                      <ListItemText primary="Not examined by the Patent Office" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: tertColor }} />
                      </ListItemIcon>
                      <ListItemText primary="Expires after 12 months if not converted to a non-provisional" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: tertColor }} />
                      </ListItemIcon>
                      <ListItemText primary="Lower initial cost but must be followed by a non-provisional application" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  mt: 2,
                  height: "100%",
                  border: `1px solid ${tertColor}33`,
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <DescriptionIcon sx={{ color: tertColor, mr: 1.5 }} />
                    <Typography
                      variant="h6"
                      fontWeight="medium"
                      color={tertColor}
                    >
                      Non-Provisional Applications
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: tertColor }} />
                      </ListItemIcon>
                      <ListItemText primary="Formal patent application that is examined by the Patent Office" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: tertColor }} />
                      </ListItemIcon>
                      <ListItemText primary="Requires formal claims, detailed specification, and drawings" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: tertColor }} />
                      </ListItemIcon>
                      <ListItemText primary="Subject to examination and office actions" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ChevronRightIcon sx={{ color: tertColor }} />
                      </ListItemIcon>
                      <ListItemText primary="Can mature into an issued patent with enforceable rights" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Caution Box for Provisional Applications */}
          <InfoBox
            title="CAUTION: Provisional Patent Application Best Practices"
            content={
              <>
                <Typography variant="body2">
                  It is highly recommended to file provisional applications with
                  the same rigor as non-provisionals, including at least one
                  fully enabled claim set, to ensure future non-provisional
                  applications can effectively claim priority. Filing overly
                  simplistic provisionals (to lower budget or for expedience) is
                  a common pitfall that can jeopardize enforcement when the
                  priority date is challenged.
                </Typography>

                <Button
                  variant="text"
                  component={RouterLink}
                  to="/news-insights?practice=patents"
                  size="small"
                  sx={{ mt: 1, color: linkColor }}
                  endIcon={<LaunchIcon />}
                >
                  View Patent Presentations
                </Button>
              </>
            }
            type="warning"
            icon={<WarningIcon />}
          />
        </Box>

        {/* Patent Process Section */}
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: priColor,
              borderBottom: `1px solid ${priColor}33`,
              pb: 1,
            }}
          >
            What to Expect in the Patent Process
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 2,
                  height: "100%",
                  border: `1px solid ${tertColor}33`,
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Patent Application Timeline
                </Typography>

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <TimelineIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Initial Consultation & Invention Disclosure"
                      secondary="We review your innovation and develop filing strategy"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <TimelineIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Prior Art Search (Optional)"
                      secondary="Assess patentability and refine claim strategy"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <TimelineIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Provisional Application Filing"
                      secondary="Establishes priority date; not examined"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <TimelineIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Non-Provisional Application Preparation & Filing"
                      secondary="Comprehensive application with claims and drawings"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <TimelineIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="USPTO Examination (18-36 months)"
                      secondary="Office actions and responses"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <TimelineIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Notice of Allowance & Issuance"
                      secondary="Patent granted with enforceable rights"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <TimelineIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Post-Grant Management"
                      secondary="Maintenance fees, continuation strategy, enforcement"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <img
                src={getImageUrl("patent-process-timeline.png")}
                alt="Patent Process Timeline"
                style={{
                  width: "100%",
                  // maxWidth: "450px",
                  // height: "auto",
                  maxHeight: "350px",
                  borderRadius: "8px",
                  display: "block",
                  margin: "0 auto",
                }}
              />

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#e8f5e9",
                  borderRadius: 2,
                  border: "1px solid #81c784",
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  gutterBottom
                  color="#2e7d32"
                >
                  Strategic Patent Planning
                </Typography>
                <Typography variant="body2">
                  We develop custom patent strategies that align with your
                  business goals, product roadmap, and competitive landscape.
                  Our engineering focus enables us to anticipate how your
                  technology will evolve, allowing us to draft patents with
                  appropriate scope to protect future developments.
                </Typography>
                <Button
                  sx={{ mt: 1, color: "#2e7d32" }}
                  endIcon={<ArrowForwardIosIcon />}
                  onClick={() => {
                    const contactSection =
                      document.getElementById("contact-section");
                    if (contactSection) {
                      contactSection.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                >
                  Schedule Strategy Session
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default KthPatentProsecution;
